import { useState, useEffect, useCallback } from 'react';
import { s, authHeaders, Toast, exportToExcel, ExcelIcon } from './listPageStyles';
import { ListPageShell, Pagination } from './ui';
import { usePageMeta } from '../hooks/usePageMeta';
import './securityScreens.css';

// G-Y-11 / G-K-09 — Denetim İzi. İki kaynağı tek ekranda gösterir:
//   Yetki Değişiklikleri → PermissionAudit  (hak başına satır: kim, kime, hangi yetkiyi verdi/aldı)
//   Komut Günlüğü        → CommandAuditLog  (genel yazma komutlarının payload'ı)
//
// Ayrı tablo olmalarının sebebi (KK-10): yetki kaydetme çoğu zaman toplu satır güncellemesidir;
// genel audit bunu tek "Update Role" kaydı olarak görür ve hangi menünün açıldığı kaybolur.

const TARGET_TYPES = { 1: 'Kullanıcı', 2: 'Kullanıcı Grubu', 3: 'Rol' };
const RIGHT_TYPES = { 1: 'Menü', 2: 'Aksiyon', 3: 'Kolon', 4: 'Veri kapsamı', 5: 'Rol ataması' };

const fmtDateTime = (v) => (v ? new Date(v).toLocaleString('tr-TR') : '-');

export function AuditLogPage() {
  usePageMeta('Denetim İzi', 'Güvenlik & İzleme');

  const [tab, setTab] = useState('permission');
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [toast, setToast] = useState(null);
  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const skip = (currentPage - 1) * pageSize;
      const base = tab === 'permission' ? '/api/permissions/audit' : '/api/AuditLog';
      const url = `${base}?$count=true&$top=${pageSize}&$skip=${skip}&$orderby=Timestamp desc`;
      const res = await fetch(url, { headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Denetim izi okunamadı');
      setRows(body?.value || body?.data || []);
      setTotalCount(body?.['@odata.count'] ?? body?.count ?? (body?.value?.length || 0));
    } catch (e) {
      showToast('error', e.message || 'Denetim izi okunamadı');
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [tab, currentPage, pageSize]);

  useEffect(() => { load(); }, [load]);

  // Sekme değişince sayfayı başa al — 5. sayfadayken kaynak değiştirmek boş liste gösterirdi.
  const switchTab = (next) => { setTab(next); setCurrentPage(1); };

  const handleExport = () => {
    const dateStr = new Date().toISOString().slice(0, 10);
    if (tab === 'permission') {
      exportToExcel(rows.map((r) => ({
        'Zaman': fmtDateTime(r.timestamp),
        'Yapan (kullanıcı Id)': r.actorUserId,
        'Hedef tipi': TARGET_TYPES[r.targetType] || r.targetType,
        'Hedef Id': r.targetId,
        'Hak tipi': RIGHT_TYPES[r.rightType] || r.rightType,
        'Hak anahtarı': r.rightKey,
        'Eski': r.oldValue, 'Yeni': r.newValue,
      })), null, `yetki-degisiklikleri_${dateStr}`);
    } else {
      exportToExcel(rows.map((r) => ({
        'Zaman': fmtDateTime(r.timestamp),
        'Kullanıcı Id': r.userId, 'İşlem': r.action,
        'Varlık': r.entityType, 'Varlık Id': r.entityId, 'Sunucu': r.machineName,
      })), null, `komut-gunlugu_${dateStr}`);
    }
    showToast('success', 'Dışa aktarıldı');
  };

  const toolbarNode = (
    <>
      <button style={s.btnSecondary} onClick={load}>Yenile</button>
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button
          type="button"
          style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
          title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}
        ><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Denetim İzi"
        subtitle="Yetki değişiklikleri ve genel komut günlüğü. Salt okunur — saklama süresi Zamanlanmış Görevler › Log / Kayıt Temizleme ekranından yönetilir."
        toolbar={toolbarNode}
        footer={(
          <Pagination
            currentPage={currentPage}
            totalCount={totalCount}
            pageSize={pageSize}
            rowCount={rows.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
          />
        )}
      >
        <div className="sec-tabs">
          <button type="button" className={`sec-tab${tab === 'permission' ? ' on' : ''}`} onClick={() => switchTab('permission')}>
            Yetki Değişiklikleri
          </button>
          <button type="button" className={`sec-tab${tab === 'command' ? ' on' : ''}`} onClick={() => switchTab('command')}>
            Komut Günlüğü
          </button>
        </div>

        {tab === 'permission' ? (
          <table className="c360-table" style={{ width: '100%' }}>
            <thead>
              <tr><th>Zaman</th><th>Yapan</th><th>Hedef</th><th>Hak tipi</th><th>Hak</th><th>Eski</th><th>Yeni</th></tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24 }}>Yükleniyor…</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24 }}>Yetki değişikliği kaydı bulunamadı</td></tr>
              )}
              {!loading && rows.map((r) => (
                <tr key={r.id}>
                  <td>{fmtDateTime(r.timestamp)}</td>
                  <td>#{r.actorUserId}</td>
                  <td>{TARGET_TYPES[r.targetType] || r.targetType} #{r.targetId}</td>
                  <td>{RIGHT_TYPES[r.rightType] || r.rightType}</td>
                  <td><span className="c360-erp-chip">{r.rightKey}</span></td>
                  <td>{r.oldValue || '-'}</td>
                  <td>{r.newValue
                    ? <span className={`sec-badge sec-badge--${r.newValue === 'VAR' ? 'ok' : 'danger'}`}>{r.newValue}</span>
                    : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="c360-table" style={{ width: '100%' }}>
            <thead>
              <tr><th>Zaman</th><th>Kullanıcı</th><th>İşlem</th><th>Varlık</th><th>Varlık Id</th><th>Sunucu</th></tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 24 }}>Yükleniyor…</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 24 }}>Komut kaydı bulunamadı</td></tr>
              )}
              {!loading && rows.map((r) => (
                <tr key={r.id}>
                  <td>{fmtDateTime(r.timestamp)}</td>
                  <td>{r.userId ? `#${r.userId}` : '-'}</td>
                  <td>{r.action || '-'}</td>
                  <td>{r.entityType || '-'}</td>
                  <td>{r.entityId || '-'}</td>
                  <td>{r.machineName || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </ListPageShell>
      <Toast toast={toast} />
    </div>
  );
}

export default AuditLogPage;
